import { useCallback, useRef } from 'react';

/**
 * Latest-request-wins guard for async fetches whose result is committed to component state.
 *
 * The scoped list fetches (ConferenceList, GroupList, ActivePackagesList, CreateGroupModal) re-fire on
 * every conference/venue change and commit their result unconditionally. Without a guard, a slow broad
 * walk (e.g. "All accessible") started first can resolve AFTER a fast narrow fetch started later,
 * overwriting the narrow result and leaving the table showing rows that don't match the selected
 * filter (finding on CR-301380769).
 *
 * Prefer {@link LatestRequest.run}: it owns the whole commit-or-drop lifecycle — sets the loading flag
 * true, awaits, and only toggles loading back off (and only invokes the commit callback) if the request
 * is still the latest on BOTH the success and error paths. The bare {@link LatestRequest.next} /
 * {@link LatestRequest.isCurrent} primitives left each call site responsible for also gating its
 * `finally { setLoading(false) }`, and in practice most sites reset loading unconditionally — clearing
 * the spinner for a superseded request while the newer one was still in flight, so the table showed the
 * previous (stale) rows as if final (finding on CR-301380769 rev5). `run` removes that footgun.
 */
export interface LatestRequest {
  /** Claim and return the next request id, making it the current one. */
  next: () => number;
  /** True only if `id` is still the most recently claimed request. */
  isCurrent: (id: number) => boolean;
  /**
   * Run a latest-request-wins fetch with owned loading-flag lifecycle.
   *
   * Claims a new request id, sets `setLoading(true)`, then awaits `work()`. If the request is still the
   * latest when `work` settles, `setLoading(false)` runs and — on success — `work`'s resolved value is
   * passed to `commit`. If a newer request has superseded this one, neither the loading flag nor `commit`
   * fires (the newer request owns them), so a slow stale walk can neither clear the spinner early nor
   * overwrite the newer result. Errors are re-thrown for the caller's `catch` only when still current;
   * a superseded error is swallowed for the same reason.
   */
  run: <T>(
    setLoading: (loading: boolean) => void,
    work: () => Promise<T>,
    commit: (result: T) => void
  ) => Promise<void>;
}

export function useLatestRequest(): LatestRequest {
  const ref = useRef(0);
  const next = useCallback(() => {
    ref.current += 1;
    return ref.current;
  }, []);
  const isCurrent = useCallback((id: number) => id === ref.current, []);
  const run = useCallback(
    async <T>(
      setLoading: (loading: boolean) => void,
      work: () => Promise<T>,
      commit: (result: T) => void
    ): Promise<void> => {
      ref.current += 1;
      const id = ref.current;
      setLoading(true);
      try {
        const result = await work();
        // Superseded by a newer request while in flight: drop the result and leave the loading flag
        // to the newer request, which owns it (finding on CR-301380769 rev5).
        if (id !== ref.current) return;
        setLoading(false);
        commit(result);
      } catch (err) {
        // A superseded error must not clear the newer request's spinner or surface to the caller.
        if (id !== ref.current) return;
        setLoading(false);
        throw err;
      }
    },
    []
  );
  return { next, isCurrent, run };
}
